import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ClipboardCheck, Plus, Loader2, Link2, X, Wand2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useDefectActions,
  type DefectActionItem,
  type ActionTaskLink,
} from "@/hooks/useDefectActions";
import {
  useAssignableProfilesForDefects,
  getProfileDisplayName,
  type AssignableProfile,
} from "@/hooks/useAssignableProfiles";
import type { ActionProgress } from "@/lib/defectAutomation";

interface DefectActionsPanelProps {
  defectId: string;
  teamId: string;
}

// fallow-ignore-file code-duplication
const NO_ASSIGNEE = "__none__";

/** Prozent-Fortschritt (0–100), 0 wenn keine Massnahmen. */
function progressPct(progress: ActionProgress): number {
  return progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
}

/**
 * TES-1743 — "Massnahmen"-Panel im Defect-Detail.
 *
 * Listet die Massnahmen (defect_action) eines Mangels, zeigt "x/y"-Fortschritt + Abschluss-Gate,
 * erlaubt Hinzufuegen (Freihand: Titel/Beschreibung/Faelligkeit/Bearbeiter), Erledigt-Toggle,
 * Loeschen, optionale PT-TSK-Verknuepfung je Massnahme sowie das Anwenden der Pflicht-Massnahmen-
 * Regeln (Vorlage -> Instanz). User-JWT-scoped (RLS greift; Writes folgen der defect_action-RLS).
 */
// fallow-ignore-next-line complexity
export function DefectActionsPanel({ defectId, teamId }: DefectActionsPanelProps) {
  const { t } = useTranslation();
  const {
    actions,
    linksByAction,
    progress,
    isLoading,
    error,
    addAction,
    setActionStatus,
    removeAction,
    applyRules,
    linkActionTask,
    unlinkActionTask,
    isMutating,
  } = useDefectActions(defectId);
  const { data: profiles = [] } = useAssignableProfilesForDefects(teamId);

  const toggleDone = useCallback(
    (action: DefectActionItem, done: boolean) => {
      setActionStatus({ actionId: action.id, status: done ? "done" : "open" });
    },
    [setActionStatus],
  );

  return (
    <section
      data-testid="defect-actions-panel"
      className="bg-card border border-outline-variant rounded-lg"
    >
      <div className="p-4 border-b border-outline-variant flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5 text-primary" aria-hidden="true" />
          <h3 className="font-display text-lg font-semibold">{t("defects.measures.title")}</h3>
        </div>
        <div className="flex items-center gap-3">
          {progress.total > 0 && (
            <span
              data-testid="action-progress-label"
              className="text-sm font-bold text-primary tabular-nums"
            >
              {t("defects.measures.progress", { done: progress.done, total: progress.total })}
            </span>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isMutating}
            onClick={() => applyRules()}
            data-testid="action-apply-rules-button"
          >
            <Wand2 className="h-4 w-4 mr-1" aria-hidden="true" />
            {t("defects.measures.applyRules")}
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {progress.total > 0 && (
          <>
            <Progress
              value={progressPct(progress)}
              aria-label={t("defects.measures.progressAria", { pct: progressPct(progress) })}
            />
            <p
              data-testid="action-gate-hint"
              className={`text-xs ${progress.open > 0 ? "text-warning" : "text-success"}`}
            >
              {progress.open > 0
                ? t("defects.measures.gateBlocked", { open: progress.open })
                : t("defects.measures.allDone")}
            </p>
          </>
        )}

        <ActionListBody
          actions={actions}
          linksByAction={linksByAction}
          profiles={profiles}
          isLoading={isLoading}
          error={error}
          isMutating={isMutating}
          onToggle={toggleDone}
          onRemove={removeAction}
          onLink={linkActionTask}
          onUnlink={unlinkActionTask}
        />

        <ActionAddForm isMutating={isMutating} profiles={profiles} onAdd={addAction} />
      </div>
    </section>
  );
}

/** Lade-/Fehler-/Leer-/Listen-Zustand der Massnahmen. */
function ActionListBody({
  actions,
  linksByAction,
  profiles,
  isLoading,
  error,
  isMutating,
  onToggle,
  onRemove,
  onLink,
  onUnlink,
}: {
  actions: DefectActionItem[];
  linksByAction: Record<string, ActionTaskLink[]>;
  profiles: AssignableProfile[];
  isLoading: boolean;
  error: Error | null;
  isMutating: boolean;
  onToggle: (action: DefectActionItem, done: boolean) => void;
  onRemove: (actionId: string) => void;
  onLink: (input: { actionId: string; ptTaskId: string }) => void;
  onUnlink: (linkId: string) => void;
}) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" aria-hidden="true" />
        {t("common.loading")}
      </div>
    );
  }
  if (error) {
    return (
      <p className="text-sm text-destructive">
        {t("common.error")}: {error.message}
      </p>
    );
  }
  if (actions.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">
        {t("defects.measures.empty")}
      </p>
    );
  }
  return (
    <ul className="space-y-2" data-testid="action-list">
      {actions.map((a) => (
        <ActionListItem
          key={a.id}
          action={a}
          links={linksByAction[a.id] ?? []}
          profiles={profiles}
          isMutating={isMutating}
          onToggle={onToggle}
          onRemove={onRemove}
          onLink={onLink}
          onUnlink={onUnlink}
        />
      ))}
    </ul>
  );
}

/** Eine Massnahmen-Zeile: Checkbox + Titel/Typ/Beschreibung + Bearbeiter/Faelligkeit + PT-TSK-Links. */
// fallow-ignore-next-line complexity
function ActionListItem({
  action,
  links,
  profiles,
  isMutating,
  onToggle,
  onRemove,
  onLink,
  onUnlink,
}: {
  action: DefectActionItem;
  links: ActionTaskLink[];
  profiles: AssignableProfile[];
  isMutating: boolean;
  onToggle: (action: DefectActionItem, done: boolean) => void;
  onRemove: (actionId: string) => void;
  onLink: (input: { actionId: string; ptTaskId: string }) => void;
  onUnlink: (linkId: string) => void;
}) {
  const { t } = useTranslation();
  const done = action.status === "done";
  const assignee = profiles.find((p) => p.user_id === action.assigned_to);

  return (
    <li className="flex flex-col gap-2 rounded border border-outline-variant px-3 py-2">
      <div className="flex items-start gap-3">
        <Checkbox
          checked={done}
          disabled={isMutating}
          onCheckedChange={(checked) => onToggle(action, checked === true)}
          aria-label={t("defects.measures.toggleAria", { title: action.title })}
          data-testid={`action-checkbox-${action.id}`}
          className="mt-0.5"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`text-sm break-words ${done ? "line-through text-muted-foreground" : ""}`}
            >
              {action.title}
            </span>
            {action.type === "qs_recheck" && (
              <Badge variant="secondary" className="text-xs">
                {t("defects.measures.qsBadge")}
              </Badge>
            )}
          </div>
          {action.description && (
            <p className="text-xs text-muted-foreground break-words mt-0.5">{action.description}</p>
          )}
          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
            {assignee && <span>{getProfileDisplayName(assignee, t("common.unknown"))}</span>}
            {action.due_date && (
              <span className="tabular-nums whitespace-nowrap">
                {new Date(action.due_date).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={isMutating}
          aria-label={t("defects.measures.removeAria", { title: action.title })}
          onClick={() => onRemove(action.id)}
          data-testid={`action-remove-${action.id}`}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      <ActionTaskLinks
        actionId={action.id}
        links={links}
        isMutating={isMutating}
        onLink={onLink}
        onUnlink={onUnlink}
      />
    </li>
  );
}

/** PT-TSK-Links einer Massnahme: Badges (mit Entfernen) + kompaktes Hinzufuegen. */
function ActionTaskLinks({
  actionId,
  links,
  isMutating,
  onLink,
  onUnlink,
}: {
  actionId: string;
  links: ActionTaskLink[];
  isMutating: boolean;
  onLink: (input: { actionId: string; ptTaskId: string }) => void;
  onUnlink: (linkId: string) => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");

  // fallow-ignore-next-line complexity
  const handleAdd = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onLink({ actionId, ptTaskId: trimmed });
    setValue("");
  }, [value, actionId, onLink]);

  return (
    <div className="pl-7 flex flex-wrap items-center gap-2">
      {links.map((l) => (
        <Badge key={l.id} variant="outline" className="gap-1 font-mono text-xs">
          <Link2 className="h-3 w-3" aria-hidden="true" />
          {l.pt_task_id}
          <button
            type="button"
            disabled={isMutating}
            onClick={() => onUnlink(l.id)}
            aria-label={t("defects.measures.links.removeAria", { task: l.pt_task_id })}
            className="ml-0.5 hover:text-destructive"
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}
      <div className="flex items-center gap-1">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={t("defects.measures.links.placeholder")}
          maxLength={64}
          className="h-7 w-40 text-xs"
          data-testid={`action-link-input-${actionId}`}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7"
          disabled={isMutating || value.trim().length === 0}
          onClick={handleAdd}
          data-testid={`action-link-button-${actionId}`}
        >
          {t("defects.measures.links.add")}
        </Button>
      </div>
    </div>
  );
}

/** Add-Formular: Titel + optional Beschreibung/Faelligkeit/Bearbeiter. */
function ActionAddForm({
  isMutating,
  profiles,
  onAdd,
}: {
  isMutating: boolean;
  profiles: AssignableProfile[];
  onAdd: (input: {
    title: string;
    description?: string | null;
    dueDate?: string | null;
    assignedTo?: string | null;
  }) => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [assignee, setAssignee] = useState<string>(NO_ASSIGNEE);

  // fallow-ignore-next-line complexity
  const handleAdd = useCallback(() => {
    const trimmed = title.trim();
    if (!trimmed) return;
    onAdd({
      title: trimmed,
      description: description.trim() || null,
      dueDate: dueDate || null,
      assignedTo: assignee === NO_ASSIGNEE ? null : assignee,
    });
    setTitle("");
    setDescription("");
    setDueDate("");
    setAssignee(NO_ASSIGNEE);
  }, [title, description, dueDate, assignee, onAdd]);

  return (
    <div className="border-t border-outline-variant pt-4 space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="action-title">{t("defects.measures.newTitleLabel")}</Label>
        <Input
          id="action-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t("defects.measures.newTitlePlaceholder")}
          maxLength={200}
          data-testid="action-title-input"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="action-description">{t("defects.measures.newDescriptionLabel")}</Label>
        <Input
          id="action-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t("defects.measures.newDescriptionPlaceholder")}
          maxLength={500}
          data-testid="action-description-input"
        />
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1.5">
          <Label htmlFor="action-due">{t("defects.measures.newDueLabel")}</Label>
          <Input
            id="action-due"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            data-testid="action-due-input"
          />
        </div>
        <div className="space-y-1.5 flex-1 min-w-[12rem]">
          <Label>{t("defects.measures.assigneeLabel")}</Label>
          <Select value={assignee} onValueChange={setAssignee}>
            <SelectTrigger data-testid="action-assignee-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_ASSIGNEE}>{t("defects.measures.assigneeNone")}</SelectItem>
              {profiles.map((p) => (
                <SelectItem key={p.user_id} value={p.user_id}>
                  {getProfileDisplayName(p, t("common.unknown"))}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          onClick={handleAdd}
          disabled={isMutating || title.trim().length === 0}
          data-testid="action-add-button"
        >
          <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
          {t("defects.measures.add")}
        </Button>
      </div>
    </div>
  );
}
